import { Link, useParams } from 'react-router-dom';
import { V2Card, V2CardBody, Icon, type SakhtarIconName } from '@sakhtar/design-system/react';
import { tenantBasePath } from '../../lib/tenant.js';

interface ReportEntry {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly icon: SakhtarIconName;
  readonly href?: string;
}

const REPORTS: readonly ReportEntry[] = [
  {
    id: 'journals',
    label: 'دفتر روزنامه',
    description: 'همهٔ اسناد قطعی به ترتیب تاریخ',
    icon: 'finance',
    href: 'journals?status=posted',
  },
  {
    id: 'general-ledger',
    label: 'دفتر کل',
    description: 'گردش و مانده‌ی هر حساب کل با همهٔ زیرحساب‌ها',
    icon: 'finance',
    href: 'reports/ledger?mode=general',
  },
  {
    id: 'subsidiary-ledger',
    label: 'دفتر معین',
    description: 'گردش و ماندهٔ جاری هر حساب معین',
    icon: 'finance',
    href: 'reports/ledger?mode=subsidiary',
  },
  {
    id: 'detail-ledger',
    label: 'دفتر تفصیلی',
    description: 'گردش هر مرکز هزینه، پروژه یا تفصیلی دیگر',
    icon: 'finance',
    href: 'reports/ledger?mode=detail',
  },
  {
    id: 'trial-balance',
    label: 'تراز آزمایشی',
    description: 'مانده حساب‌ها در تاریخ معین',
    icon: 'finance',
    href: 'reports/trial-balance',
  },
  {
    id: 'trial-balance-columns',
    label: 'تراز ۴ و ۸ ستونی',
    description: 'تراز ۲/۴/۶/۸ ستونی با افتتاحیه، گردش و مانده؛ به تفکیک سطح',
    icon: 'finance',
    href: 'reports/trial-balance-columns',
  },
  {
    id: 'income-statement',
    label: 'صورت سود و زیان',
    description: 'درآمد و هزینهٔ دوره',
    icon: 'finance',
    href: 'reports/income-statement',
  },
  {
    id: 'balance-sheet',
    label: 'ترازنامه',
    description: 'دارایی، بدهی و حقوق مالکانه',
    icon: 'finance',
    href: 'reports/balance-sheet',
  },
  {
    id: 'ar-aging',
    label: 'سن مطالبات',
    description: 'دریافتنی‌ها به تفکیک بازهٔ سررسید',
    icon: 'finance',
    href: 'ar',
  },
  {
    id: 'ap-aging',
    label: 'سن بدهی',
    description: 'پرداختنی‌ها به تفکیک بازهٔ سررسید',
    icon: 'finance',
    href: 'ap',
  },
  {
    id: 'cashflow',
    label: 'صورت جریان وجوه نقد',
    description:
      'جریان‌های نقدی ناشی از فعالیت‌های عملیاتی، سرمایه‌گذاری و تأمین مالی (استاندارد ۲ ایران)',
    icon: 'finance',
    href: 'reports/cash-flow',
  },
  {
    id: 'profitability',
    label: 'سودآوری به تفکیک ابعاد',
    description: 'تحلیل درآمد، هزینه و حاشیه سود بر اساس مراکز هزینه، پروژه‌ها، مشتریان و کالاها',
    icon: 'finance',
    href: 'reports/profitability',
  },
];

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Finance.dc.html §11 "REPORTS LIST · 560px"
 * archetype: static list of 7 report types, each real-or-honestly-
 *   unavailable
 * Updated 2026-10-07 (finance gap closure): ledgers (کل/معین/تفصیلی,
 *   `GET .../finance/reports/ledger`) and the 2/4/6/8-column trial balance
 *   (`GET .../finance/reports/trial-balance-columns`) now have real
 *   endpoints and pages, linked above.
 * status: PARTIAL_REAL — originally only "تراز آزمایشی" had a dedicated report
 *   endpoint (`GET .../finance/reports/trial-balance`, ERP-168) and its
 *   own page. "دفتر روزنامه" (journal book) and the two aging reports
 *   already exist as real pages under a different tab
 *   (JournalsPage/OpenItemsPage) — linked here, not duplicated.
 *   Income statement (`GET .../finance/reports/income-statement`) and
 *   balance sheet (`GET .../finance/reports/balance-sheet`) now have
 *   full backend endpoints, UI routes, and printable views (PR #284 / #293).
 *   Only cash-flow currently has no backend ("این گزارش در نسخهٔ فعلی ارائه نشده است").
 *   This hub serves as the central directory for active financial statements.
 * state reference: n/a (static index)
 */
export function ReportsPage() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) return null;
  const base = tenantBasePath(slug);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
        گزارش‌های مالی — {REPORTS.filter((r) => r.href).length} گزارش فعال
      </div>
      <div
        style={{
          display: 'grid',
          gap: 14,
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}
      >
        {REPORTS.map((report) => {
          const body = (
            <V2Card>
              <V2CardBody>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span
                    style={{
                      color: report.href ? 'var(--sk-brand-600)' : 'var(--sk-fg-subtle)',
                      display: 'flex',
                    }}
                  >
                    <Icon name={report.icon} size="md" />
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>{report.label}</span>
                    <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                      {report.description}
                    </span>
                  </div>
                </div>
              </V2CardBody>
            </V2Card>
          );
          return report.href ? (
            <Link
              key={report.id}
              to={`${base}/finance/${report.href}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {body}
            </Link>
          ) : (
            <div key={report.id} style={{ opacity: 0.55, cursor: 'not-allowed' }}>
              {body}
            </div>
          );
        })}
      </div>
    </div>
  );
}
